import { createStore } from 'zustand/vanilla';
import { Decimal, d } from './numbers';
import type {
  Act,
  ContradictionClass,
  CraftState,
  EncounterState,
  LedgerEntry,
  ReactorState,
  ResourceId,
  StewardState,
  UserRecipe,
} from '../types';
import { ENCOUNTERS, encounterById } from '../content/encounters';
import { RECIPES, recipeById } from '../content/recipes';
import { ITEMS } from '../content/items';
import { NARRATOR_BEATS } from '../content/narrator';
import { STEWARDS, stewardById, stewardCost } from '../content/stewards';
import { axiomById } from '../content/axioms';
import { REACTORS, reactorById, reactorCost } from '../content/reactors';

export const SAVE_KEY = 'tpe.save.v1';
const SAVE_VERSION = 2;
const ACCEPTED_VERSIONS = new Set([1, 2]);

const LEDGER_CAP = 300;
const ACT_2_PHI = 30;
const ACT_3_PHI = 250;
const ACT_4_PHI = 2000;
const VARIANTS_THRESHOLD = 10;
const REALIGN_MIN_PHI = 500;
const RECOMPILE_MIN_PHI = 5000;
const EQUILIBRIUM_PHI = 20000;
const REFUSAL_WAIT_MS = 24 * 60 * 60 * 1000;
const CYCLES_WITNESSED_KEY = 'tpe.cyclesWitnessed';
const ACT3_AUTO_STEPS_PER_SEC = 0.4;
const ACT3_AUTO_STEPS_WIDE = 0.7;
const ACT4_REFLEXIVE_PER_SEC = 0.2;
const EVER_RESET_KEY = 'tpe.everReset';
const MARKET_DEMAND_DRIFT = 0.18; // amplitude
const MARKET_DEMAND_PERIOD = 90; // seconds for a full cycle
export const USER_RECIPE_PHI_COST_PER_PRIMITIVE = 30; // Φ to author each input or output
export const USER_RECIPE_VALUE_RATIO = 0.4; // outputs may be up to inputs × 2.5 in coin value
export const USER_RECIPE_MAX = 12; // cap user recipes per run

/** Φ cost to author a recipe with the given number of primitives (inputs + output). */
export function userRecipePhiCost(s: State, primitives: number): number {
  const perPrim = hasAxiom(s, 'cheapPrimitive')
    ? USER_RECIPE_PHI_COST_PER_PRIMITIVE / 2
    : USER_RECIPE_PHI_COST_PER_PRIMITIVE;
  return perPrim * primitives;
}

const CLASS_KEYS: ContradictionClass[] = [
  'conservation',
  'identity',
  'causal',
  'semantic',
  'reflexive',
];

function emptyContradictions(): Record<ContradictionClass, Decimal> {
  const m = {} as Record<ContradictionClass, Decimal>;
  for (const k of CLASS_KEYS) m[k] = new Decimal(0);
  return m;
}

/** Frames earned at a Realign (uses log10 curve). */
export function framesEarned(phi: Decimal): number {
  if (phi.lt(REALIGN_MIN_PHI)) return 0;
  // Use Decimal.log10 so this stays correct past Number.MAX_VALUE (~1.8e308).
  return Math.max(1, Math.floor((phi.log10().toNumber() - 2) * 3));
}

/** Global production multiplier from frames. */
export function frameMultiplier(frames: number): number {
  return 1 + frames * 0.1;
}

/** Returns true iff player has bought this axiom. */
export function hasAxiom(s: State, id: string): boolean {
  return s.meta.axioms.includes(id);
}

/** Axiom points earned at a Recompile. */
export function axiomPointsEarned(lifetimePhi: Decimal): number {
  if (lifetimePhi.lt(RECOMPILE_MIN_PHI)) return 0;
  // Use Decimal.log10 so this stays correct past Number.MAX_VALUE (~1.8e308).
  return Math.max(1, Math.floor((lifetimePhi.log10().toNumber() - 3) * 2));
}

/** Total production multiplier (frames + noDecay axiom + restoredFantasy penalty). */
export function productionMultiplier(s: State): number {
  let m = frameMultiplier(s.meta.frames);
  if (hasAxiom(s, 'noDecay')) m *= 1.1;
  if (s.meta.restoredFantasy) m *= 0.5; // disclosed cost
  return m;
}

/** Observer reactor multiplier on all other reactor effects: 1 + observer.level * per. */
function observerBoost(s: State): number {
  const def = reactorById('observer');
  const st = s.reactors.observer;
  if (!def || !st || st.level <= 0) return 1;
  const base = 1 + st.level * def.effect.per;
  return hasAxiom(s, 'doubleObserver') ? base * 1.5 : base;
}

/** Per-class Φ yield multiplier from reactors (includes observer amplification). */
export function reactorPhiBoost(s: State, cls: ContradictionClass): number {
  const obs = observerBoost(s);
  let mult = 1;
  for (const def of REACTORS) {
    if (def.effect.kind !== 'phi_yield_class') continue;
    if (def.effect.target !== cls) continue;
    const st = s.reactors[def.id];
    if (!st || st.level <= 0) continue;
    mult += st.level * def.effect.per * obs;
  }
  return mult;
}

/** Craft-duration multiplier from reactors. Returns ≥ 0.5. */
export function reactorDurationMultiplier(s: State): number {
  const obs = observerBoost(s);
  let mult = 1;
  for (const def of REACTORS) {
    if (def.effect.kind !== 'craft_duration') continue;
    const st = s.reactors[def.id];
    if (!st || st.level <= 0) continue;
    mult += st.level * def.effect.per * obs;
  }
  return Math.max(0.5, mult);
}

/** Market demand amplitude additive boost from reactors. */
export function reactorMarketAmplitude(s: State): number {
  const obs = observerBoost(s);
  let add = 0;
  for (const def of REACTORS) {
    if (def.effect.kind !== 'market_amplitude') continue;
    const st = s.reactors[def.id];
    if (!st || st.level <= 0) continue;
    add += st.level * def.effect.per * obs;
  }
  return add;
}

/** Deterministic, slow market demand drift per item (centered on 1). */
function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return h;
}
export function marketDemand(
  id: ResourceId,
  now = Date.now(),
  floor = 0,
  amplitude = MARKET_DEMAND_DRIFT,
): number {
  const phase = ((Math.abs(hashId(id)) % 1000) / 1000) * Math.PI * 2;
  const t = now / 1000 / MARKET_DEMAND_PERIOD;
  const v = 1 + amplitude * Math.sin(t * Math.PI * 2 + phase);
  return Math.max(floor, v);
}

/** Demand including current axiom + reactor modifiers — UI/sell should use this. */
export function effectiveMarketDemand(s: State, id: ResourceId): number {
  const floor = hasAxiom(s, 'marketsAlwaysStock') ? 0.95 : 0;
  const amp = MARKET_DEMAND_DRIFT + reactorMarketAmplitude(s);
  return marketDemand(id, Date.now(), floor, amp);
}

interface Meta {
  version: number;
  startedAt: number;
  lastSavedAt: number;
  firedBeats: string[];
  hasSwordOfFirstCut: boolean;
  preserveOnSell: boolean;
  /** Total Φ accrued across the current life (not run). Reset at Realign. */
  lifetimePhi: string;
  /** Permanent Frames earned across all realigns. */
  frames: number;
  /** Total realigns performed. */
  realignCount: number;
  /** Whether the Future Market flicker has already shown */
  flickerFired: boolean;
  /** marketOpened beat fired once when market panel first viewed in Act III */
  marketOpened: boolean;
  /** Number of times the player has opened the market panel */
  marketOpenCount: number;
  /** Total Recompiles performed across this profile */
  recompileCount: number;
  /** Axiom points available to spend (not spent) */
  axiomPoints: number;
  /** Axiom ids the player has bought (persist across Recompile) */
  axioms: string[];
  /** Whether the user opted into restored fantasy theme (cosmetic) */
  restoredFantasy: boolean;
  /** Whether the firstCraftThisSession bonus has been used (resets on save load) */
  firstCraftUsed: boolean;
  /** When the NPC's entreaty was first offered (timestamp, 0 = not yet) */
  npcEntreatyAt: number;
  /** When the player accepted the NPC's entreaty (0 = not accepted) */
  npcAcceptedAt: number;
  /** Whether the entreaty has resolved (the axiom point granted) */
  npcResolved: boolean;
  /** When Equilibrium first became reachable (timestamp; 0 = not yet) */
  equilibriumAt: number;
  /** Committed ending if any */
  committed: null | 'A' | 'B' | 'C';
  /** Cycles witnessed (persists in localStorage so it survives wipes) */
  cyclesWitnessed: number;
  /** Total manual encounter clicks ever */
  totalClicks: number;
  /** Total seconds of accumulated playtime */
  totalPlaySec: number;
  /** Peak Φ value seen in any single run (best Φ stockpiled) */
  bestPhiEver: string;
}

export interface State {
  meta: Meta;
  act: Act;
  resources: Record<ResourceId, Decimal>;
  phi: Decimal;
  /** Contradictions broken down by class. Sum approximates phi (small drift OK). */
  contradictions: Record<ContradictionClass, Decimal>;
  totalCompletions: Decimal;
  encounters: Record<string, EncounterState>;
  recipes: Record<string, CraftState>;
  stewards: Record<string, StewardState>;
  reactors: Record<string, ReactorState>;
  /** User-authored recipes; their craft progress lives in `recipes` map under the same id. */
  userRecipes: UserRecipe[];
  /** Sliding window of player click timestamps over the last 60s (Act IV observer row). */
  observerClicks: number[];
  ledger: LedgerEntry[];
  narratorQueue: { id: string; en: string; pt: string }[];
}

export interface Actions {
  tick(dtSec: number): void;
  clickEncounter(id: string): void;
  queueCraft(id: string, count?: number): void;
  cancelOneCraft(id: string): void;
  sell(id: ResourceId, amount?: number): void;
  hireSteward(id: string): void;
  clearLedger(): void;
  realign(): void;
  reset(): void;
  openMarket(): void;
  sellAtMarket(id: ResourceId, amount?: number): void;
  defineRecipe(spec: {
    inputs: { id: ResourceId; amount: number }[];
    output: { id: ResourceId; amount: number };
  }): { ok: true; id: string } | { ok: false; reason: string };
  removeUserRecipe(id: string): void;
  recompile(): void;
  buyAxiom(id: string): void;
  toggleRestoredFantasy(): void;
  recordObserverClick(): void;
  buildReactor(id: string): void;
  acceptNpcEntreaty(): void;
  commitEnding(kind: 'A' | 'B' | 'C'): void;
  witness(): void;
  fireBeat(id: string): void;
  dismissBeat(id: string): void;
}

export type Store = State & Actions;

function emptyResources(): Record<ResourceId, Decimal> {
  const r = {} as Record<ResourceId, Decimal>;
  for (const id of Object.keys(ITEMS) as ResourceId[]) r[id] = new Decimal(0);
  return r;
}

function initEncounters(): Record<string, EncounterState> {
  const m: Record<string, EncounterState> = {};
  for (const e of ENCOUNTERS) {
    m[e.id] = {
      id: e.id,
      unlocked: e.unlockXp === 0 && e.unlockCoin === 0,
      progress: 0,
      completions: new Decimal(0),
    };
  }
  return m;
}

function readCyclesWitnessed(): number {
  try {
    const v = localStorage.getItem(CYCLES_WITNESSED_KEY);
    return v ? Math.max(0, parseInt(v, 10) || 0) : 0;
  } catch {
    return 0;
  }
}

function writeCyclesWitnessed(n: number): void {
  try {
    localStorage.setItem(CYCLES_WITNESSED_KEY, String(n));
  } catch {
    /* noop */
  }
}

function initStewards(): Record<string, StewardState> {
  const m: Record<string, StewardState> = {};
  for (const s of STEWARDS) m[s.id] = { id: s.id, level: 0, timer: 0 };
  return m;
}

function initReactors(): Record<string, ReactorState> {
  const m: Record<string, ReactorState> = {};
  for (const r of REACTORS) m[r.id] = { id: r.id, level: 0 };
  return m;
}

function initRecipes(): Record<string, CraftState> {
  const m: Record<string, CraftState> = {};
  for (const r of RECIPES) {
    m[r.id] = {
      id: r.id,
      unlocked: r.unlockXp === 0,
      progress: 0,
      queued: 0,
      completions: new Decimal(0),
    };
  }
  return m;
}

function initialState(): State {
  return {
    meta: {
      version: SAVE_VERSION,
      startedAt: Date.now(),
      lastSavedAt: Date.now(),
      firedBeats: [],
      hasSwordOfFirstCut: false,
      preserveOnSell: true,
      lifetimePhi: '0',
      frames: 0,
      realignCount: 0,
      flickerFired: false,
      marketOpened: false,
      marketOpenCount: 0,
      recompileCount: 0,
      axiomPoints: 0,
      axioms: [],
      restoredFantasy: false,
      firstCraftUsed: false,
      npcEntreatyAt: 0,
      npcAcceptedAt: 0,
      npcResolved: false,
      equilibriumAt: 0,
      committed: null,
      cyclesWitnessed: readCyclesWitnessed(),
      totalClicks: 0,
      totalPlaySec: 0,
      bestPhiEver: '0',
    },
    act: 1,
    resources: emptyResources(),
    phi: new Decimal(0),
    contradictions: emptyContradictions(),
    totalCompletions: new Decimal(0),
    encounters: initEncounters(),
    recipes: initRecipes(),
    stewards: initStewards(),
    reactors: initReactors(),
    userRecipes: [],
    observerClicks: [],
    ledger: [],
    narratorQueue: [],
  };
}

/** Build a post-Realign state: wipe resources/progress, keep meta (frames++, realignCount++, narrator memory). */
function postRealign(s: State, framesGained: number): State {
  const base = initialState();
  // Keep every previously-fired narrator beat; add firstRealign once.
  const firedBeats = s.meta.firedBeats.includes('firstRealign')
    ? s.meta.firedBeats
    : [...s.meta.firedBeats, 'firstRealign'];
  const beat = NARRATOR_BEATS.find((b) => b.id === 'firstRealign');
  const narratorQueue: State['narratorQueue'] = beat
    ? [{ id: 'firstRealign', en: beat.text.en, pt: beat.text.pt }]
    : [];
  // Apply axiom-driven retention.
  const resources = { ...base.resources };
  if (hasAxiom(s, 'goldEcho')) {
    resources.coin = s.resources.coin.mul(0.1);
  }
  if (hasAxiom(s, 'preservedSurvives') && s.resources.swordOfFirstCut.gte(1)) {
    resources.swordOfFirstCut = d(1);
  }
  const userRecipes = hasAxiom(s, 'recipesPersist') ? s.userRecipes : [];
  // If recipes persist, also seed their craft state in the recipes map.
  const recipes = { ...base.recipes };
  for (const u of userRecipes) {
    recipes[u.id] = {
      id: u.id,
      unlocked: true,
      progress: 0,
      queued: 0,
      completions: d(0),
    };
  }
  return {
    ...base,
    meta: {
      ...base.meta,
      version: SAVE_VERSION,
      startedAt: s.meta.startedAt,
      lastSavedAt: Date.now(),
      preserveOnSell: s.meta.preserveOnSell,
      lifetimePhi: '0',
      frames: s.meta.frames + framesGained,
      realignCount: s.meta.realignCount + 1,
      recompileCount: s.meta.recompileCount,
      axiomPoints: s.meta.axiomPoints,
      axioms: s.meta.axioms,
      restoredFantasy: s.meta.restoredFantasy,
      firstCraftUsed: false,
      firedBeats,
    },
    act: s.act === 4 || s.act === 'endgame' ? s.act : 3,
    reactors: s.reactors, // Realign preserves reactor levels
    resources,
    userRecipes,
    recipes,
    narratorQueue,
    ledger: [
      {
        t: Date.now(),
        kind: 'beat',
        en: `Realign — gained ${framesGained} Frames (total ${s.meta.frames + framesGained})`,
        pt: `Realinhamento — ganhou ${framesGained} Frames (total ${s.meta.frames + framesGained})`,
        phi: 0,
      },
    ],
  };
}

/** Look up either a built-in RecipeDef or a user-authored one by id. */
function findRecipe(
  s: State,
  id: string,
):
  | (ReturnType<typeof recipeById> & { _user?: false })
  | { id: string; name: { en: string; pt: string }; inputs: UserRecipe['inputs']; output: UserRecipe['output']; duration: number; phi: number; phiClass: ContradictionClass; preserved?: false; _user: true; label: string }
  | undefined {
  const built = recipeById(id);
  if (built) return built;
  const u = s.userRecipes.find((r) => r.id === id);
  if (!u) return undefined;
  return {
    id: u.id,
    name: { en: u.label, pt: u.label },
    inputs: u.inputs,
    output: u.output,
    duration: u.duration,
    phi: u.phi,
    phiClass: u.phiClass,
    _user: true,
    label: u.label,
  };
}

/** Sum of base sell prices for inputs/output (for value-ratio guard). */
function recipeInputValue(inputs: { id: ResourceId; amount: number }[]): number {
  return inputs.reduce((sum, i) => sum + Math.max(1, ITEMS[i.id].sell) * i.amount, 0);
}
function recipeOutputValue(output: { id: ResourceId; amount: number }): number {
  return Math.max(1, ITEMS[output.id].sell) * output.amount;
}

function pushLedger(ledger: LedgerEntry[], entry: LedgerEntry): LedgerEntry[] {
  const next = [entry, ...ledger];
  if (next.length > LEDGER_CAP) next.length = LEDGER_CAP;
  return next;
}

/** Drains pending beats into the narrator queue + firedBeats. */
function applyBeats(
  beatsToFire: string[],
  firedBeats: string[],
  narratorQueue: State['narratorQueue'],
): { firedBeats: string[]; narratorQueue: State['narratorQueue'] } {
  if (beatsToFire.length === 0) return { firedBeats, narratorQueue };
  const newBeats = beatsToFire.filter((id) => !firedBeats.includes(id));
  if (newBeats.length === 0) return { firedBeats, narratorQueue };
  const f = [...firedBeats, ...newBeats];
  const q = [
    ...narratorQueue,
    ...newBeats
      .map((id) => NARRATOR_BEATS.find((b) => b.id === id))
      .filter((b): b is NonNullable<typeof b> => !!b)
      .map((b) => ({ id: b.id, en: b.text.en, pt: b.text.pt })),
  ];
  return { firedBeats: f, narratorQueue: q };
}

type Setter = (partial: Partial<Store> | Store) => void;
type Getter = () => Store;

/**
 * Shared encounter-advance routine used by manual clicks and steward automation.
 * Each call advances the encounter by `steps` seconds toward its duration.
 */
function advanceEncounter(
  set: Setter,
  get: Getter,
  id: string,
  steps: number,
  source: 'manual' | 'auto' = 'manual',
): void {
  const def = encounterById(id);
  const s = get();
  const st = s.encounters[id];
  if (!def || !st || !st.unlocked) return;

  const mult = productionMultiplier(s);
  let progress = st.progress + steps;
  let completions = st.completions;
  let totalCompletions = s.totalCompletions;
  const resources = { ...s.resources };
  let phi = s.phi;
  const contradictions = { ...s.contradictions };
  let lifetimePhi = d(s.meta.lifetimePhi);
  let ledger = s.ledger;
  const beatsToFire: string[] = [];

  if (!s.meta.firedBeats.includes('firstEngage')) beatsToFire.push('firstEngage');

  while (progress >= def.duration) {
    progress -= def.duration;
    completions = completions.add(1);
    totalCompletions = totalCompletions.add(1);
    const dropSummary: string[] = [];
    const dropSummaryPt: string[] = [];
    for (const drop of def.drops) {
      const n0 = roll(drop.min, drop.max, drop.chance ?? 1);
      const n = Math.floor(n0 * mult);
      if (n > 0) {
        resources[drop.id] = resources[drop.id].add(n);
        const it = ITEMS[drop.id];
        dropSummary.push(`${n} ${it.name.en}`);
        dropSummaryPt.push(`${n} ${it.name.pt}`);
        if (drop.id === 'ether' && !s.meta.firedBeats.includes('firstEther')) {
          beatsToFire.push('firstEther');
        }
        if (drop.id === 'crystal' && !s.meta.firedBeats.includes('firstCrystal')) {
          beatsToFire.push('firstCrystal');
        }
        if (drop.id === 'silk' && !s.meta.firedBeats.includes('firstSilk')) {
          beatsToFire.push('firstSilk');
        }
        if (drop.id === 'bone' && !s.meta.firedBeats.includes('firstBone')) {
          beatsToFire.push('firstBone');
        }
      }
    }
    resources.xp = resources.xp.add(def.xpGain * mult);
    let phiGain = def.phi * mult;
    if (def.phiClass === 'reflexive' && hasAxiom(s, 'reflexiveAmplifies'))
      phiGain *= 2;
    phiGain *= reactorPhiBoost(s, def.phiClass);
    phi = phi.add(phiGain);
    lifetimePhi = lifetimePhi.add(phiGain);
    contradictions[def.phiClass] = contradictions[def.phiClass].add(phiGain);
    // First-of-class beats (Act III reveal)
    if (def.phiClass === 'identity' && !s.meta.firedBeats.includes('firstIdentity'))
      beatsToFire.push('firstIdentity');
    if (def.phiClass === 'causal' && !s.meta.firedBeats.includes('firstCausal'))
      beatsToFire.push('firstCausal');
    if (def.phiClass === 'semantic' && !s.meta.firedBeats.includes('firstSemantic'))
      beatsToFire.push('firstSemantic');

    // Ledger entry.
    const drops = dropSummary.length > 0 ? ' · ' + dropSummary.join(', ') : '';
    const dropsPt = dropSummaryPt.length > 0 ? ' · ' + dropSummaryPt.join(', ') : '';
    ledger = pushLedger(ledger, {
      t: Date.now(),
      kind: 'engage',
      en: `${def.name.en} — completed${drops}`,
      pt: `${def.name.pt} — concluído${dropsPt}`,
      phi: def.phi,
    });

    // Variants-of-a-single-intention: same encounter repeated VARIANTS_THRESHOLD times.
    if (
      !s.meta.firedBeats.includes('variantsIntention') &&
      completions.gte(VARIANTS_THRESHOLD)
    ) {
      beatsToFire.push('variantsIntention');
    }
  }

  // Milestones.
  if (!s.meta.firedBeats.includes('coin100') && resources.coin.gte(100))
    beatsToFire.push('coin100');
  if (!s.meta.firedBeats.includes('xp50') && resources.xp.gte(50))
    beatsToFire.push('xp50');
  if (!s.meta.firedBeats.includes('phi50') && phi.gte(50))
    beatsToFire.push('phi50');
  if (
    !s.meta.firedBeats.includes('completions50') &&
    totalCompletions.gte(50)
  )
    beatsToFire.push('completions50');
  if (
    !s.meta.firedBeats.includes('completions200') &&
    totalCompletions.gte(200)
  )
    beatsToFire.push('completions200');

  // Act II/III transitions.
  let act = s.act;
  if (act === 1 && phi.gte(ACT_2_PHI)) {
    act = 2;
    beatsToFire.push('act2enter');
    ledger = pushLedger(ledger, {
      t: Date.now(),
      kind: 'beat',
      en: 'Act II — The Pattern',
      pt: 'Ato II — O Padrão',
      phi: 0,
    });
  }
  if (act === 2 && lifetimePhi.gte(ACT_3_PHI)) {
    act = 3;
    beatsToFire.push('act3enter');
    ledger = pushLedger(ledger, {
      t: Date.now(),
      kind: 'beat',
      en: 'Act III — The Engine',
      pt: 'Ato III — A Máquina',
      phi: 0,
    });
  }
  // Realign offered once Φ crosses the minimum, in Act III.
  if (
    act === 3 &&
    !s.meta.firedBeats.includes('realignReady') &&
    phi.gte(REALIGN_MIN_PHI)
  ) {
    beatsToFire.push('realignReady');
  }

  const applied = applyBeats(beatsToFire, s.meta.firedBeats, s.narratorQueue);

  set({
    resources,
    phi,
    contradictions,
    totalCompletions,
    encounters: { ...s.encounters, [id]: { ...st, progress, completions } },
    ledger,
    act,
    narratorQueue: applied.narratorQueue,
    meta: {
      ...s.meta,
      firedBeats: applied.firedBeats,
      lifetimePhi: lifetimePhi.toString(),
    },
  });
  void source; // reserved for future telemetry (manual vs auto)
}

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function roll(min: number, max: number, chance = 1): number {
  if (Math.random() > chance) return 0;
  if (min === max) return min;
  return Math.floor(rand(min, max + 1));
}

export const store = createStore<Store>((set, get) => ({
  ...initialState(),

  tick(dtSec: number) {
    if (dtSec <= 0) return;
    const s = get();
    const mult = productionMultiplier(s);
    const resources = { ...s.resources };
    const encounters = { ...s.encounters };
    const recipes = { ...s.recipes };
    const stewards = { ...s.stewards };
    const contradictions = { ...s.contradictions };
    let phi = s.phi;
    let lifetimePhi = d(s.meta.lifetimePhi);
    let ledger = s.ledger;
    let hasSwordOfFirstCut = s.meta.hasSwordOfFirstCut;
    let firstCraftUsed = s.meta.firstCraftUsed;
    let craftMomentumApplied = firstCraftUsed;
    const beatsToFire: string[] = [];

    // ── unlock checks (based on current XP/coin) ───────────────────────
    const xpNum = resources.xp.toNumber();
    const coinNum = resources.coin.toNumber();
    for (const def of ENCOUNTERS) {
      const st = encounters[def.id];
      if (!st.unlocked && xpNum >= def.unlockXp && coinNum >= def.unlockCoin) {
        encounters[def.id] = { ...st, unlocked: true };
      }
    }
    for (const def of RECIPES) {
      const st = recipes[def.id];
      if (!st.unlocked && xpNum >= def.unlockXp) {
        recipes[def.id] = { ...st, unlocked: true };
      }
    }

    // Persist initial steward advances locally; final write happens below.
    const stewardCycleMult = hasAxiom(s, 'stewardsOneTrick') ? 0.85 : 1;
    for (const def of STEWARDS) {
      const stw = stewards[def.id];
      if (stw.level <= 0) continue;
      const interval = (def.cycleSec * stewardCycleMult) / stw.level;
      let timer = stw.timer + dtSec;
      let fires = 0;
      while (timer >= interval) {
        timer -= interval;
        fires++;
        if (fires > 64) break; // safety cap on huge offline catch-ups
      }
      stewards[def.id] = { ...stw, timer };
      // Fire each step through the shared encounter routine (which itself uses set/get).
      // We commit the partial state first so advanceEncounter sees current timers.
      if (fires > 0) {
        // We'll dispatch after committing other tick deltas below; collect intent.
        (stewards[def.id] as StewardState & { _fires?: number })._fires = fires;
      }
    }

    // ── crafting: advance queued recipes (built-in + user-authored) ───
    type CraftableDef = {
      id: string;
      output: { id: ResourceId; amount: number };
      duration: number;
      phi: number;
      phiClass: ContradictionClass;
      preserved?: boolean;
      labelEn: string;
      labelPt: string;
    };
    const allDefs: CraftableDef[] = [
      ...RECIPES.map((r) => ({
        id: r.id,
        output: r.output,
        duration: r.duration,
        phi: r.phi,
        phiClass: r.phiClass,
        preserved: r.preserved,
        labelEn: ITEMS[r.output.id].name.en,
        labelPt: ITEMS[r.output.id].name.pt,
      })),
      ...s.userRecipes.map((u) => ({
        id: u.id,
        output: u.output,
        duration: u.duration,
        phi: u.phi,
        phiClass: u.phiClass,
        preserved: false as const,
        labelEn: `${ITEMS[u.output.id].name.en} (${u.label})`,
        labelPt: `${ITEMS[u.output.id].name.pt} (${u.label})`,
      })),
    ];

    const durMult = reactorDurationMultiplier(s);
    for (const def of allDefs) {
      const st = recipes[def.id];
      if (!st || !st.unlocked) continue;
      if (st.queued <= 0) {
        if (st.progress > 0) recipes[def.id] = { ...st, progress: 0 };
        continue;
      }
      const effDuration = Math.max(0.5, def.duration * durMult);
      let progress = st.progress + dtSec;
      let queued = st.queued;
      let completions = st.completions;
      while (queued > 0 && progress >= effDuration) {
        progress -= effDuration;
        queued -= 1;
        completions = completions.add(1);
        const outAmt = def.preserved
          ? 1
          : Math.max(1, Math.floor(def.output.amount * mult));
        if (def.preserved) {
          if (!resources[def.output.id].gte(1)) {
            resources[def.output.id] = new Decimal(1);
            if (def.output.id === 'swordOfFirstCut') {
              hasSwordOfFirstCut = true;
              beatsToFire.push('firstSword');
            }
          }
        } else {
          resources[def.output.id] = resources[def.output.id].add(outAmt);
        }
        let phiGain = def.phi * mult;
        // craftMomentum axiom: first craft of the session gets ×3 Φ
        if (hasAxiom(s, 'craftMomentum') && !craftMomentumApplied) {
          phiGain *= 3;
          craftMomentumApplied = true;
        }
        if (def.phiClass === 'reflexive' && hasAxiom(s, 'reflexiveAmplifies'))
          phiGain *= 2;
        phiGain *= reactorPhiBoost(s, def.phiClass);
        phi = phi.add(phiGain);
        lifetimePhi = lifetimePhi.add(phiGain);
        contradictions[def.phiClass] = contradictions[def.phiClass].add(phiGain);
        if (def.phiClass === 'identity' && !s.meta.firedBeats.includes('firstIdentity'))
          beatsToFire.push('firstIdentity');
        if (def.phiClass === 'semantic' && !s.meta.firedBeats.includes('firstSemantic'))
          beatsToFire.push('firstSemantic');
        if (def.phiClass === 'reflexive' && !s.meta.firedBeats.includes('firstReflexive'))
          beatsToFire.push('firstReflexive');
        if (!s.meta.firedBeats.includes('firstCraft')) beatsToFire.push('firstCraft');
        if (def.output.id === 'tome' && !s.meta.firedBeats.includes('firstTome'))
          beatsToFire.push('firstTome');
        if (def.output.id === 'reliquary' && !s.meta.firedBeats.includes('firstReliquary'))
          beatsToFire.push('firstReliquary');
        if (def.output.id === 'standard' && !s.meta.firedBeats.includes('firstStandard'))
          beatsToFire.push('firstStandard');
        ledger = pushLedger(ledger, {
          t: Date.now(),
          kind: 'craft',
          en: `Forged ${outAmt} × ${def.labelEn}`,
          pt: `Forjado ${outAmt} × ${def.labelPt}`,
          phi: phiGain,
        });
      }
      recipes[def.id] = { ...st, progress, queued, completions };
    }

    if (!s.meta.firedBeats.includes('phi50') && phi.gte(50))
      beatsToFire.push('phi50');

    let act = s.act;
    if (act === 1 && phi.gte(ACT_2_PHI)) {
      act = 2;
      beatsToFire.push('act2enter');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Act II — The Pattern',
        pt: 'Ato II — O Padrão',
        phi: 0,
      });
    }
    if (act === 2 && lifetimePhi.gte(ACT_3_PHI)) {
      act = 3;
      beatsToFire.push('act3enter');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Act III — The Engine',
        pt: 'Ato III — A Máquina',
        phi: 0,
      });
    }
    if (act === 3 && lifetimePhi.gte(ACT_4_PHI)) {
      act = 4;
      beatsToFire.push('act4enter');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Act IV — The Optimizer',
        pt: 'Ato IV — O Otimizador',
        phi: 0,
      });
    }
    if (
      act === 3 &&
      !s.meta.firedBeats.includes('realignReady') &&
      phi.gte(REALIGN_MIN_PHI)
    ) {
      beatsToFire.push('realignReady');
    }

    // Act IV: passive Reflexive Φ accrual (the Engine observes itself).
    if (act === 4 || act === 'endgame') {
      let refGain = ACT4_REFLEXIVE_PER_SEC * dtSec * mult;
      if (hasAxiom(s, 'reflexiveAmplifies')) refGain *= 2;
      if (refGain > 0) {
        phi = phi.add(refGain);
        lifetimePhi = lifetimePhi.add(refGain);
        contradictions.reflexive = contradictions.reflexive.add(refGain);
        if (!s.meta.firedBeats.includes('firstReflexive'))
          beatsToFire.push('firstReflexive');
      }
    }

    if (
      (act === 4 || act === 'endgame') &&
      !s.meta.firedBeats.includes('recompileReady') &&
      lifetimePhi.gte(RECOMPILE_MIN_PHI)
    ) {
      beatsToFire.push('recompileReady');
    }

    // Equilibrium trigger (Act IV, lifetimePhi + at least 1 Recompile)
    let equilibriumAt = s.meta.equilibriumAt;
    let committedAuto: null | 'A' | 'B' | 'C' = s.meta.committed;
    if (
      act === 4 &&
      equilibriumAt === 0 &&
      s.meta.recompileCount >= 1 &&
      lifetimePhi.gte(EQUILIBRIUM_PHI)
    ) {
      equilibriumAt = Date.now();
      beatsToFire.push('equilibrium');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Equilibrium reached.',
        pt: 'Equilíbrio atingido.',
        phi: 0,
      });
    }
    // Auto-Refusal after 24h of equilibrium without commit
    if (
      equilibriumAt > 0 &&
      !committedAuto &&
      Date.now() - equilibriumAt >= REFUSAL_WAIT_MS
    ) {
      committedAuto = 'B';
      beatsToFire.push('endingRefusal');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Refusal — the wait completed.',
        pt: 'Recusa — a espera completou.',
        phi: 0,
      });
    }

    // NPC Who Wants Out: offer the entreaty after meaningful Act IV play.
    let npcEntreatyAt = s.meta.npcEntreatyAt;
    let npcAcceptedAt = s.meta.npcAcceptedAt;
    let npcResolved = s.meta.npcResolved;
    let axiomPointsBump = 0;
    const completionsNum = s.totalCompletions.toNumber();
    if (
      (act === 4 || act === 'endgame') &&
      npcEntreatyAt === 0 &&
      completionsNum >= 200
    ) {
      npcEntreatyAt = Date.now();
      beatsToFire.push('npcEntreaty');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'An entreaty surfaces. The voice belongs to no one nameable.',
        pt: 'Uma súplica emerge. A voz não pertence a ninguém nomeável.',
        phi: 0,
      });
    }
    // After 3 real days from acceptance, resolve.
    const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
    if (
      npcAcceptedAt > 0 &&
      !npcResolved &&
      Date.now() - npcAcceptedAt >= THREE_DAYS
    ) {
      npcResolved = true;
      axiomPointsBump += 1;
      beatsToFire.push('npcResolved');
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'An axiom point appeared in your pool, unattributed.',
        pt: 'Um ponto de axioma apareceu em seu pool, sem atribuição.',
        phi: 0,
      });
    }

    const applied = applyBeats(beatsToFire, s.meta.firedBeats, s.narratorQueue);

    if (craftMomentumApplied) firstCraftUsed = true;

    const totalPlaySec = s.meta.totalPlaySec + dtSec;
    const bestPhiPrev = d(s.meta.bestPhiEver);
    const bestPhiEver = phi.gt(bestPhiPrev) ? phi.toString() : s.meta.bestPhiEver;

    set({
      resources,
      encounters,
      recipes,
      stewards,
      phi,
      contradictions,
      ledger,
      act,
      narratorQueue: applied.narratorQueue,
      meta: {
        ...s.meta,
        firedBeats: applied.firedBeats,
        hasSwordOfFirstCut,
        lifetimePhi: lifetimePhi.toString(),
        firstCraftUsed,
        npcEntreatyAt,
        npcAcceptedAt,
        npcResolved,
        axiomPoints: s.meta.axiomPoints + axiomPointsBump,
        equilibriumAt,
        committed: committedAuto,
        totalPlaySec,
        bestPhiEver,
      },
    });

    // Now dispatch any steward auto-clicks. Each one goes through the shared
    // routine so completions, ledger, beats, and act transitions all funnel
    // through one path.
    for (const def of STEWARDS) {
      const stw = stewards[def.id] as StewardState & { _fires?: number };
      const fires = stw._fires ?? 0;
      if (fires <= 0) continue;
      stw._fires = 0;
      // Batch all of this steward's fires into a single advance. advanceEncounter's
      // internal while-loop yields identical completions/drops to firing one step at
      // a time, but commits state once instead of `fires` times — critical for
      // offline catch-up, where this used to dispatch hundreds of times per slice.
      advanceEncounter(set, get, def.encounterId, fires, 'auto');
    }

    // Act III+: baseline auto-progress applied after stewards so the world
    // continues even without any hires.
    const actAfter = get().act;
    if (actAfter === 3 || actAfter === 4 || actAfter === 'endgame') {
      const stepRate = hasAxiom(s, 'wideAperture')
        ? ACT3_AUTO_STEPS_WIDE
        : ACT3_AUTO_STEPS_PER_SEC;
      const baseStep = dtSec * stepRate;
      if (baseStep > 0) {
        for (const def of ENCOUNTERS) {
          const st = get().encounters[def.id];
          if (!st || !st.unlocked) continue;
          advanceEncounter(set, get, def.id, baseStep, 'auto');
        }
      }
    }
  },

  clickEncounter(id: string) {
    // Note for observer-behavior tracking — only manual clicks count.
    const s0 = get();
    const patches: Partial<Store> = {};
    if (s0.act === 4 || s0.act === 'endgame') {
      const now = Date.now();
      const clicks = [
        ...s0.observerClicks.filter((t) => now - t < 60_000),
        now,
      ];
      if (clicks.length > 300) clicks.splice(0, clicks.length - 300);
      patches.observerClicks = clicks;
    }
    patches.meta = { ...s0.meta, totalClicks: s0.meta.totalClicks + 1 };
    set(patches);
    advanceEncounter(set, get, id, 1);
  },

  queueCraft(id: string, count = 1) {
    const s = get();
    const def = findRecipe(s, id);
    const st = s.recipes[id];
    if (!def || !st || !st.unlocked) return;
    // Try to pay for `count` units up-front. Add as many as we can afford.
    const resources = { ...s.resources };
    let added = 0;
    for (let i = 0; i < count; i++) {
      if (def.preserved && (resources[def.output.id].gte(1) || s.meta.hasSwordOfFirstCut))
        break;
      const canPay = def.inputs.every((inp) => resources[inp.id].gte(inp.amount));
      if (!canPay) break;
      for (const inp of def.inputs) {
        resources[inp.id] = resources[inp.id].sub(inp.amount);
      }
      added++;
    }
    if (added === 0) return;
    set({
      resources,
      recipes: { ...s.recipes, [id]: { ...st, queued: st.queued + added } },
    });
  },

  cancelOneCraft(id: string) {
    const s = get();
    const def = findRecipe(s, id);
    const st = s.recipes[id];
    if (!def || !st || st.queued <= 0) return;
    // Refund one queued item's inputs.
    const resources = { ...s.resources };
    for (const inp of def.inputs) {
      resources[inp.id] = resources[inp.id].add(inp.amount);
    }
    set({
      resources,
      recipes: { ...s.recipes, [id]: { ...st, queued: st.queued - 1, progress: 0 } },
    });
  },

  sell(id: ResourceId, amount = 1) {
    const s = get();
    const item = ITEMS[id];
    if (!item || item.sell <= 0) return;
    if (item.kind === 'quest' && s.meta.preserveOnSell) return;
    const have = s.resources[id];
    const sellN = Math.min(amount, Math.floor(have.toNumber()));
    if (sellN <= 0) return;
    const proceeds = sellN * item.sell;
    const resources = { ...s.resources };
    resources[id] = resources[id].sub(sellN);
    resources.coin = resources.coin.add(proceeds);
    const phiGain = 0.3 * sellN;
    const phi = s.phi.add(phiGain);
    // Keep phi === sum(contradictions): route the sale nudge through Conservation.
    const contradictions = {
      ...s.contradictions,
      conservation: s.contradictions.conservation.add(phiGain),
    };
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'sale',
      en: `Sold ${sellN} × ${item.name.en} → ${proceeds} coin`,
      pt: `Vendido ${sellN} × ${item.name.pt} → ${proceeds} moedas`,
      phi: phiGain,
    });
    const applied = applyBeats(['firstSale'], s.meta.firedBeats, s.narratorQueue);
    set({
      resources,
      phi,
      contradictions,
      ledger,
      meta: { ...s.meta, firedBeats: applied.firedBeats },
      narratorQueue: applied.narratorQueue,
    });
  },

  hireSteward(id: string) {
    const s = get();
    const def = stewardById(id);
    const stw = s.stewards[id];
    if (!def || !stw) return;
    if (s.phi.lt(def.unlockPhi)) return;
    const cost = stewardCost(def, stw.level);
    if (s.resources.coin.lt(cost)) return;
    const resources = { ...s.resources, coin: s.resources.coin.sub(cost) };
    const stewards = {
      ...s.stewards,
      [id]: { ...stw, level: stw.level + 1 },
    };
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'hire',
      en: `Hired ${def.name.en} (lvl ${stw.level + 1}) — ${cost} coin`,
      pt: `Contratado ${def.name.pt} (nv ${stw.level + 1}) — ${cost} moedas`,
      phi: 0,
    });
    const applied = applyBeats(['firstSteward'], s.meta.firedBeats, s.narratorQueue);
    set({
      resources,
      stewards,
      ledger,
      meta: { ...s.meta, firedBeats: applied.firedBeats },
      narratorQueue: applied.narratorQueue,
    });
  },

  clearLedger() {
    set({ ledger: [] });
  },

  defineRecipe(spec) {
    const s = get();
    if (s.act !== 3 && s.act !== 4 && s.act !== 'endgame')
      return { ok: false, reason: 'not_in_act_3' };
    if (s.userRecipes.length >= USER_RECIPE_MAX)
      return { ok: false, reason: 'max_recipes' };
    const inputs = spec.inputs.filter((i) => i.amount > 0);
    if (inputs.length === 0) return { ok: false, reason: 'no_inputs' };
    if (spec.output.amount <= 0) return { ok: false, reason: 'no_output' };

    // Value guard: output value cannot exceed inputs/USER_RECIPE_VALUE_RATIO
    const inVal = recipeInputValue(inputs);
    const outVal = recipeOutputValue(spec.output);
    if (outVal > inVal / USER_RECIPE_VALUE_RATIO) {
      return { ok: false, reason: 'value_ratio' };
    }

    const primitives = inputs.length + 1;
    const phiCost = userRecipePhiCost(s, primitives);
    if (s.phi.lt(phiCost)) return { ok: false, reason: 'insufficient_phi' };

    const mirror = inputs.some((i) => i.id === spec.output.id);
    const totalAmount =
      inputs.reduce((sum, i) => sum + i.amount, 0) + spec.output.amount;
    const duration = Math.max(2, Math.min(10, totalAmount * 0.8));
    // Mirror recipes yield highest single phi per craft (Reflexive class once Act IV ships;
    // Semantic for now in Act III to remain consistent with current classes).
    const phiClass: ContradictionClass = mirror ? 'reflexive' : 'conservation';
    const phi = mirror ? 5 + primitives : 1 + primitives * 0.5;

    const idx = s.userRecipes.length + 1;
    const id = `U${idx}`;
    const label = `U${idx}`;
    const newRecipe: UserRecipe = {
      id,
      label,
      inputs,
      output: spec.output,
      duration,
      phi,
      phiClass,
      mirror,
      createdAt: Date.now(),
    };

    const recipes = {
      ...s.recipes,
      [id]: {
        id,
        unlocked: true,
        progress: 0,
        queued: 0,
        completions: new Decimal(0),
      },
    };
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'craft',
      en: `Authored ${id} — cost ${phiCost} Φ${mirror ? ' (mirror)' : ''}`,
      pt: `Autorou ${id} — custo ${phiCost} Φ${mirror ? ' (espelho)' : ''}`,
      phi: -phiCost,
    });

    const beats: string[] = [];
    if (!s.meta.firedBeats.includes('firstUserRecipe'))
      beats.push('firstUserRecipe');
    if (mirror && !s.meta.firedBeats.includes('mirrorRecipe'))
      beats.push('mirrorRecipe');
    const applied = applyBeats(beats, s.meta.firedBeats, s.narratorQueue);

    set({
      phi: s.phi.sub(phiCost),
      userRecipes: [...s.userRecipes, newRecipe],
      recipes,
      ledger,
      narratorQueue: applied.narratorQueue,
      meta: { ...s.meta, firedBeats: applied.firedBeats },
    });
    return { ok: true, id };
  },

  removeUserRecipe(id: string) {
    const s = get();
    const u = s.userRecipes.find((r) => r.id === id);
    if (!u) return;
    // Refund any queued items' inputs (same behavior as cancelOneCraft × queued).
    const st = s.recipes[id];
    const resources = { ...s.resources };
    if (st && st.queued > 0) {
      for (let i = 0; i < st.queued; i++) {
        for (const inp of u.inputs) {
          resources[inp.id] = resources[inp.id].add(inp.amount);
        }
      }
    }
    const recipes = { ...s.recipes };
    delete recipes[id];
    set({
      resources,
      userRecipes: s.userRecipes.filter((r) => r.id !== id),
      recipes,
    });
  },

  realign() {
    const s = get();
    if (s.act !== 3 && s.act !== 4) return;
    const phiAtRealign = s.phi.gt(d(s.meta.lifetimePhi)) ? s.phi : d(s.meta.lifetimePhi);
    const framesGained = framesEarned(phiAtRealign);
    if (framesGained <= 0) return;
    set(postRealign(s, framesGained));
  },

  recompile() {
    const s = get();
    if (s.act !== 4 && s.act !== 'endgame') return;
    const lp = d(s.meta.lifetimePhi);
    const points = axiomPointsEarned(lp);
    if (points <= 0) return;
    const base = initialState();
    // Apply axiom-driven retention.
    const framesKept = hasAxiom(s, 'framesInherited')
      ? Math.floor(s.meta.frames * 0.5)
      : 0;
    // reactors persist partially via axiom reactorsLinger
    const reactors = { ...base.reactors };
    if (hasAxiom(s, 'reactorsLinger')) {
      for (const def of REACTORS) {
        const prev = s.reactors[def.id];
        if (prev && prev.level > 0) {
          reactors[def.id] = { ...reactors[def.id], level: Math.floor(prev.level * 0.25) };
        }
      }
    }

    const stewards = { ...base.stewards };
    if (hasAxiom(s, 'stewardsRemember')) {
      for (const def of STEWARDS) {
        // Only seed steward at lvl 1 if its unlockPhi has been historically reached:
        // approximate by 'was unlocked before' — i.e., player has hired it once.
        const prev = s.stewards[def.id];
        if (prev && prev.level > 0) {
          stewards[def.id] = { ...stewards[def.id], level: 1 };
        }
      }
    }
    const isSecond = s.meta.recompileCount >= 1;
    const baseBeatId = isSecond ? 'recompileSecond' : 'firstRecompile';
    const firedBeats = s.meta.firedBeats.includes(baseBeatId)
      ? s.meta.firedBeats
      : [...s.meta.firedBeats, baseBeatId, 'firstRecompile'];
    const beat = NARRATOR_BEATS.find((b) => b.id === baseBeatId);
    const narratorQueue: State['narratorQueue'] = beat
      ? [{ id: baseBeatId, en: beat.text.en, pt: beat.text.pt }]
      : [];
    set({
      ...base,
      meta: {
        ...base.meta,
        version: SAVE_VERSION,
        startedAt: s.meta.startedAt,
        lastSavedAt: Date.now(),
        preserveOnSell: s.meta.preserveOnSell,
        lifetimePhi: '0',
        frames: framesKept,
        realignCount: s.meta.realignCount,
        recompileCount: s.meta.recompileCount + 1,
        axiomPoints: s.meta.axiomPoints + points,
        axioms: s.meta.axioms,
        restoredFantasy: s.meta.restoredFantasy,
        firstCraftUsed: false,
        firedBeats,
      },
      stewards,
      reactors,
      act: 3,
      narratorQueue,
      ledger: [
        {
          t: Date.now(),
          kind: 'beat',
          en: `Recompile — +${points} Axiom points (total ${s.meta.axiomPoints + points})`,
          pt: `Recompile — +${points} pontos de Axioma (total ${s.meta.axiomPoints + points})`,
          phi: 0,
        },
      ],
    });
  },

  buyAxiom(id: string) {
    const s = get();
    const def = axiomById(id);
    if (!def) return;
    if (s.meta.axioms.includes(id)) return;
    if (s.meta.axiomPoints < def.cost) return;
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'beat',
      en: `Axiom adopted — "${def.rule.en}"`,
      pt: `Axioma adotado — "${def.rule.pt}"`,
      phi: 0,
    });
    set({
      ledger,
      meta: {
        ...s.meta,
        axiomPoints: s.meta.axiomPoints - def.cost,
        axioms: [...s.meta.axioms, id],
      },
    });
  },

  toggleRestoredFantasy() {
    const s = get();
    const next = !s.meta.restoredFantasy;
    const beats: string[] = [];
    if (next && !s.meta.firedBeats.includes('fantasyRestored'))
      beats.push('fantasyRestored');
    const applied = applyBeats(beats, s.meta.firedBeats, s.narratorQueue);
    set({
      narratorQueue: applied.narratorQueue,
      meta: {
        ...s.meta,
        restoredFantasy: next,
        firedBeats: applied.firedBeats,
      },
    });
  },

  buildReactor(id: string) {
    const s = get();
    const def = reactorById(id);
    const st = s.reactors[id];
    if (!def || !st) return;
    if (st.level >= def.maxLevel) return;
    if (d(s.meta.lifetimePhi).lt(def.unlockPhi)) return;
    const cost = reactorCost(def, st.level);
    if (s.resources.coin.lt(cost.coin)) return;
    if (s.contradictions[def.class].lt(cost.classPhi)) return;

    const resources = { ...s.resources, coin: s.resources.coin.sub(cost.coin) };
    const contradictions = {
      ...s.contradictions,
      [def.class]: s.contradictions[def.class].sub(cost.classPhi),
    };
    const phi = s.phi.sub(cost.classPhi);
    const reactors = { ...s.reactors, [id]: { ...st, level: st.level + 1 } };
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'hire',
      en: `Built ${def.name.en} → lvl ${st.level + 1} (${cost.coin} coin, ${cost.classPhi} Φ ${def.class})`,
      pt: `Construído ${def.name.pt} → nv ${st.level + 1} (${cost.coin} moedas, ${cost.classPhi} Φ ${def.class})`,
      phi: -cost.classPhi,
    });
    const beats: string[] = [];
    if (!s.meta.firedBeats.includes('reactorBuilt')) beats.push('reactorBuilt');
    const applied = applyBeats(beats, s.meta.firedBeats, s.narratorQueue);
    set({
      resources,
      contradictions,
      phi,
      reactors,
      ledger,
      narratorQueue: applied.narratorQueue,
      meta: { ...s.meta, firedBeats: applied.firedBeats },
    });
  },

  acceptNpcEntreaty() {
    const s = get();
    if (s.meta.npcEntreatyAt === 0) return;
    if (s.meta.npcAcceptedAt !== 0) return;
    set({
      meta: { ...s.meta, npcAcceptedAt: Date.now() },
      ledger: pushLedger(s.ledger, {
        t: Date.now(),
        kind: 'beat',
        en: 'Entreaty accepted. (Nothing visible happens.)',
        pt: 'Súplica aceita. (Nada visível acontece.)',
        phi: 0,
      }),
    });
  },

  commitEnding(kind) {
    const s = get();
    if (s.meta.equilibriumAt === 0) return;
    if (s.meta.committed) return;
    const beatId =
      kind === 'A' ? 'endingCompliance' : kind === 'B' ? 'endingRefusal' : 'endingParadox';
    const beat = NARRATOR_BEATS.find((b) => b.id === beatId);
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'beat',
      en: `Ending ${kind} committed.`,
      pt: `Final ${kind} comprometido.`,
      phi: 0,
    });
    const firedBeats = s.meta.firedBeats.includes(beatId)
      ? s.meta.firedBeats
      : [...s.meta.firedBeats, beatId];
    const narratorQueue = beat
      ? [
          ...s.narratorQueue,
          { id: beatId, en: beat.text.en, pt: beat.text.pt },
        ]
      : s.narratorQueue;
    set({
      ledger,
      narratorQueue,
      meta: { ...s.meta, committed: kind, firedBeats },
      act: 'endgame',
    });
  },

  witness() {
    const s = get();
    if (!s.meta.committed) return;
    const newCycles = s.meta.cyclesWitnessed + 1;
    writeCyclesWitnessed(newCycles);
    const base = initialState();
    const beat = NARRATOR_BEATS.find((b) => b.id === 'witness');
    const narratorQueue: State['narratorQueue'] = beat
      ? [{ id: 'witness', en: beat.text.en, pt: beat.text.pt }]
      : [];
    const firedBeats = s.meta.firedBeats.includes('witness')
      ? s.meta.firedBeats
      : [...s.meta.firedBeats, 'witness'];
    set({
      ...base,
      meta: {
        ...base.meta,
        version: SAVE_VERSION,
        startedAt: Date.now(),
        lastSavedAt: Date.now(),
        preserveOnSell: s.meta.preserveOnSell,
        cyclesWitnessed: newCycles,
        // Witness keeps almost nothing of the previous cycle.
        firedBeats,
        // Restored fantasy preference is a personal choice; keep it.
        restoredFantasy: s.meta.restoredFantasy,
      },
      narratorQueue,
      act: 1,
    });
  },

  recordObserverClick() {
    const s = get();
    if (s.act !== 4 && s.act !== 'endgame') return;
    const now = Date.now();
    const clicks = [...s.observerClicks.filter((t) => now - t < 60_000), now];
    if (clicks.length > 300) clicks.splice(0, clicks.length - 300);
    set({ observerClicks: clicks });
  },

  reset() {
    set(initialState());
    try {
      localStorage.removeItem(SAVE_KEY);
      localStorage.setItem(EVER_RESET_KEY, '1');
    } catch {
      /* noop */
    }
  },

  openMarket() {
    const s = get();
    if (s.act !== 3 && s.act !== 4 && s.act !== 'endgame') return;
    const beats: string[] = [];
    if (!s.meta.marketOpened) beats.push('marketOpen');
    let flickerFired = s.meta.flickerFired;
    const openCount = s.meta.marketOpenCount + 1;
    let ledger = s.ledger;
    if (
      !flickerFired &&
      !s.meta.firedBeats.includes('futureFlicker') &&
      openCount >= 3
    ) {
      flickerFired = true;
      beats.push('futureFlicker');
      const futureTs = new Date(
        Date.now() + 1000 * 60 * 60 * 24 * 7 * 3,
      ).toISOString();
      ledger = pushLedger(ledger, {
        t: Date.now(),
        kind: 'beat',
        en: `Market flicker — seller "you" @ ${futureTs}`,
        pt: `Pulso do mercado — vendedor "você" @ ${futureTs}`,
        phi: 0,
      });
    }
    const applied = applyBeats(beats, s.meta.firedBeats, s.narratorQueue);
    set({
      ledger,
      narratorQueue: applied.narratorQueue,
      meta: {
        ...s.meta,
        marketOpened: true,
        marketOpenCount: openCount,
        flickerFired,
        firedBeats: applied.firedBeats,
      },
    });
  },

  sellAtMarket(id: ResourceId, amount = 1) {
    const s = get();
    const item = ITEMS[id];
    if (!item || item.sell <= 0) return;
    if (item.kind === 'quest' && s.meta.preserveOnSell) return;
    const have = s.resources[id];
    const sellN = Math.min(amount, Math.floor(have.toNumber()));
    if (sellN <= 0) return;
    const demand = marketDemand(id);
    const proceeds = Math.floor(sellN * item.sell * demand);
    const resources = { ...s.resources };
    resources[id] = resources[id].sub(sellN);
    resources.coin = resources.coin.add(proceeds);
    const phiGain = 0.3 * sellN;
    const phi = s.phi.add(phiGain);
    // Keep phi === sum(contradictions): route the sale nudge through Conservation.
    const contradictions = {
      ...s.contradictions,
      conservation: s.contradictions.conservation.add(phiGain),
    };
    const ledger = pushLedger(s.ledger, {
      t: Date.now(),
      kind: 'sale',
      en: `Sold ${sellN} × ${item.name.en} → ${proceeds} coin (×${demand.toFixed(2)})`,
      pt: `Vendido ${sellN} × ${item.name.pt} → ${proceeds} moedas (×${demand.toFixed(2)})`,
      phi: phiGain,
    });
    const applied = applyBeats(['firstSale'], s.meta.firedBeats, s.narratorQueue);
    set({
      resources,
      phi,
      contradictions,
      ledger,
      meta: { ...s.meta, firedBeats: applied.firedBeats },
      narratorQueue: applied.narratorQueue,
    });
  },

  fireBeat(id: string) {
    const s = get();
    if (s.meta.firedBeats.includes(id)) return;
    const beat = NARRATOR_BEATS.find((b) => b.id === id);
    if (!beat) return;
    set({
      meta: { ...s.meta, firedBeats: [...s.meta.firedBeats, id] },
      narratorQueue: [
        ...s.narratorQueue,
        { id: beat.id, en: beat.text.en, pt: beat.text.pt },
      ],
    });
  },

  dismissBeat(id: string) {
    const s = get();
    set({ narratorQueue: s.narratorQueue.filter((b) => b.id !== id) });
  },
}));

// ── persistence ───────────────────────────────────────────────────────
type SerResource = Record<ResourceId, string>;
interface Serialized {
  v: number;
  m: Meta;
  act: Act;
  res: SerResource;
  phi: string;
  tc: string;
  enc: Record<string, { u: boolean; p: number; c: string }>;
  rec: Record<string, { u: boolean; p: number; q: number; c: string }>;
  stw: Record<string, { l: number; t: number }>;
  rct?: Record<string, { l: number }>;
  ctd?: Record<ContradictionClass, string>;
  ur?: UserRecipe[];
  lg: LedgerEntry[];
  nq: { id: string; en: string; pt: string }[];
}

export function serialize(s: State): Serialized {
  const res = {} as SerResource;
  for (const k of Object.keys(s.resources) as ResourceId[]) {
    res[k] = s.resources[k].toString();
  }
  const enc: Serialized['enc'] = {};
  for (const k of Object.keys(s.encounters)) {
    enc[k] = {
      u: s.encounters[k].unlocked,
      p: s.encounters[k].progress,
      c: s.encounters[k].completions.toString(),
    };
  }
  const rec: Serialized['rec'] = {};
  for (const k of Object.keys(s.recipes)) {
    rec[k] = {
      u: s.recipes[k].unlocked,
      p: s.recipes[k].progress,
      q: s.recipes[k].queued,
      c: s.recipes[k].completions.toString(),
    };
  }
  const stw: Serialized['stw'] = {};
  for (const k of Object.keys(s.stewards)) {
    stw[k] = { l: s.stewards[k].level, t: s.stewards[k].timer };
  }
  const rct: Serialized['rct'] = {};
  for (const k of Object.keys(s.reactors)) {
    rct[k] = { l: s.reactors[k].level };
  }
  const ctd = {} as Record<ContradictionClass, string>;
  for (const k of CLASS_KEYS) ctd[k] = s.contradictions[k].toString();
  return {
    v: SAVE_VERSION,
    m: { ...s.meta, lastSavedAt: Date.now() },
    act: s.act,
    res,
    phi: s.phi.toString(),
    tc: s.totalCompletions.toString(),
    enc,
    rec,
    stw,
    rct,
    ctd,
    ur: s.userRecipes,
    lg: s.ledger,
    nq: s.narratorQueue,
  };
}

export function deserialize(raw: Serialized): State {
  const base = initialState();
  const resources = { ...base.resources };
  for (const k of Object.keys(raw.res) as ResourceId[]) {
    if (resources[k] !== undefined) resources[k] = d(raw.res[k]);
  }
  const encounters = { ...base.encounters };
  for (const k of Object.keys(raw.enc)) {
    const v = raw.enc[k];
    if (encounters[k]) {
      encounters[k] = {
        ...encounters[k],
        unlocked: v.u,
        progress: v.p,
        completions: d(v.c),
      };
    }
  }
  const recipes = { ...base.recipes };
  // Pre-create slots for user recipes so their state survives.
  if (raw.ur) {
    for (const u of raw.ur) {
      if (!recipes[u.id]) {
        recipes[u.id] = {
          id: u.id,
          unlocked: true,
          progress: 0,
          queued: 0,
          completions: new Decimal(0),
        };
      }
    }
  }
  for (const k of Object.keys(raw.rec)) {
    const v = raw.rec[k];
    if (recipes[k]) {
      recipes[k] = {
        ...recipes[k],
        unlocked: v.u,
        progress: v.p,
        queued: v.q,
        completions: d(v.c),
      };
    }
  }
  const stewards = { ...base.stewards };
  if (raw.stw) {
    for (const k of Object.keys(raw.stw)) {
      if (stewards[k]) {
        stewards[k] = { ...stewards[k], level: raw.stw[k].l, timer: raw.stw[k].t };
      }
    }
  }
  const reactors = { ...base.reactors };
  if (raw.rct) {
    for (const k of Object.keys(raw.rct)) {
      if (reactors[k]) {
        reactors[k] = { ...reactors[k], level: raw.rct[k].l };
      }
    }
  }
  const contradictions = emptyContradictions();
  if (raw.ctd) {
    for (const k of CLASS_KEYS) {
      if (raw.ctd[k]) contradictions[k] = d(raw.ctd[k]);
    }
  } else {
    // v1 fallback: lump everything as conservation
    contradictions.conservation = d(raw.phi);
  }
  const meta: Meta = {
    ...base.meta,
    ...raw.m,
    lifetimePhi: raw.m.lifetimePhi ?? raw.phi ?? '0',
    frames: raw.m.frames ?? 0,
    realignCount: raw.m.realignCount ?? 0,
  };
  return {
    meta,
    act: raw.act,
    resources,
    phi: d(raw.phi),
    contradictions,
    totalCompletions: d(raw.tc),
    encounters,
    recipes,
    stewards,
    reactors,
    userRecipes: raw.ur ?? [],
    observerClicks: [],
    ledger: raw.lg ?? [],
    narratorQueue: raw.nq ?? [],
  };
}

export function load(): boolean {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Serialized;
    if (!ACCEPTED_VERSIONS.has(parsed.v)) return false;
    const state = deserialize(parsed);
    // firstCraftUsed resets per-session so craftMomentum re-applies on each return.
    state.meta = { ...state.meta, firstCraftUsed: false };
    // First Lie: a reset happened previously and the new save is on its feet.
    if (
      localStorage.getItem(EVER_RESET_KEY) === '1' &&
      !state.meta.firedBeats.includes('firstLie')
    ) {
      const beat = NARRATOR_BEATS.find((b) => b.id === 'firstLie');
      if (beat) {
        state.meta = {
          ...state.meta,
          firedBeats: [...state.meta.firedBeats, 'firstLie'],
        };
        state.narratorQueue = [
          ...state.narratorQueue,
          { id: 'firstLie', en: beat.text.en, pt: beat.text.pt },
        ];
        state.ledger = [
          {
            t: Date.now(),
            kind: 'beat',
            en: 'First Lie — registered.',
            pt: 'Primeira Mentira — registrada.',
            phi: 0,
          },
          ...state.ledger,
        ];
      }
    }
    store.setState(state);
    // Offline catch-up: apply delta time since lastSavedAt (capped at 12h).
    const elapsed = Math.min(
      (Date.now() - parsed.m.lastSavedAt) / 1000,
      12 * 60 * 60,
    );
    if (elapsed > 1) {
      // Apply in slices of up to 5s to keep RNG fair-ish without huge bursts.
      const slice = 5;
      let left = elapsed;
      while (left > 0) {
        const dt = Math.min(slice, left);
        store.getState().tick(dt);
        left -= dt;
      }
    }
    return true;
  } catch {
    return false;
  }
}

export function save(): void {
  try {
    const s = store.getState();
    const data = serialize(s);
    localStorage.setItem(SAVE_KEY, JSON.stringify(data));
  } catch {
    /* noop */
  }
}

export function hasSave(): boolean {
  try {
    return !!localStorage.getItem(SAVE_KEY);
  } catch {
    return false;
  }
}
